'use strict';

/**
 * Product Categories & Store Domain Classification
 *
 * Shared module for product type categorization, store domain archetype
 * classification, and domain-specific configuration.
 *
 * Extracted from web-enricher (Phase 9b) and extended for
 * Adaptive Customer Intelligence.
 */

// ============================================
// Product Type Category Mapping
// ============================================

const CATEGORY_MAP = {
  beauty_skincare: [
    'foundation', 'moisturizer', 'serum', 'cleanser', 'sunscreen', 'lipstick',
    'mascara', 'concealer', 'primer', 'toner', 'exfoliant', 'mask', 'face mask',
    'eye cream', 'lip balm', 'blush', 'bronzer', 'highlighter', 'skincare',
    'beauty', 'cosmetic', 'makeup', 'lotion', 'cream', 'oil', 'spf',
    'retinol', 'vitamin c serum', 'hyaluronic', 'bb cream', 'cc cream',
    'tinted moisturizer', 'setting spray', 'face mist'
  ],
  apparel: [
    't-shirt', 'tee', 'shirt', 'dress', 'jacket', 'pants', 'jeans', 'shorts',
    'skirt', 'blouse', 'sweater', 'hoodie', 'coat', 'blazer', 'vest',
    'leggings', 'joggers', 'cardigan', 'romper', 'jumpsuit', 'top', 'bottom',
    'shoes', 'sneakers', 'boots', 'sandals', 'heels', 'flats', 'loafers',
    'socks', 'underwear', 'bra', 'swimwear', 'bikini', 'activewear',
    'hat', 'cap', 'scarf', 'gloves', 'belt'
  ],
  electronics: [
    'headphones', 'earbuds', 'speaker', 'charger', 'cable', 'adapter',
    'phone case', 'screen protector', 'power bank', 'keyboard', 'mouse',
    'monitor', 'webcam', 'microphone', 'tripod', 'light', 'ring light',
    'smart watch', 'fitness tracker', 'tablet', 'laptop', 'computer'
  ],
  food_wellness: [
    'supplement', 'protein', 'vitamin', 'tea', 'coffee', 'collagen',
    'probiotic', 'omega', 'magnesium', 'zinc', 'iron', 'multivitamin',
    'powder', 'capsule', 'gummy', 'tincture', 'herbal', 'organic',
    'superfood', 'snack', 'bar', 'drink', 'juice', 'smoothie'
  ],
  home_lifestyle: [
    'candle', 'blanket', 'pillow', 'throw', 'rug', 'curtain', 'towel',
    'sheet', 'duvet', 'comforter', 'vase', 'planter', 'frame', 'mirror',
    'organizer', 'storage', 'basket', 'tray', 'coaster', 'mug', 'tumbler',
    'diffuser', 'incense', 'soap', 'hand wash', 'room spray'
  ]
};

/**
 * Get product category from productType string
 */
function getProductCategory(productType) {
  if (!productType) return 'generic';

  const normalized = productType.toLowerCase().trim();

  for (const [category, keywords] of Object.entries(CATEGORY_MAP)) {
    for (const keyword of keywords) {
      if (normalized.includes(keyword) || keyword.includes(normalized)) {
        return category;
      }
    }
  }

  return 'generic';
}

// ============================================
// Store Domain Archetypes
// ============================================

const ARCHETYPE_NAMES = {
  fashion_boutique: 'Fashion Boutique',
  beauty_wellness: 'Beauty & Wellness',
  home_decor: 'Home & Decor',
  electronics_tech: 'Electronics & Tech',
  gift_specialty: 'Gift & Specialty',
  luxury_premium: 'Luxury & Premium',
  consumables: 'Consumables',
  general_retail: 'General Retail'
};

/**
 * Archetype configurations with domain-specific thresholds.
 *
 * CRITICAL: `general_retail` MUST match all current hardcoded values
 * for backward compatibility. Services fall back to general_retail
 * when storeDomain is absent from workspace.
 */
const ARCHETYPE_CONFIGS = Object.freeze({

  // ── General Retail (backward-compatible defaults) ──────────────
  general_retail: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 10, minSpend: 2000 }),
      loyal: Object.freeze({ minOrders: 5, minSpend: 500 }),
      atRiskDays: 90,
      churnedDays: 180
    }),
    valueSegments: Object.freeze({ vip: 2000, high: 500, medium: 100 }),
    enabledSignals: Object.freeze({
      sizing: true,
      reorderCandidates: true,
      stylePreference: true,
      ingredientSensitivity: false,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['bargain_hunter', 'loyal_regular', 'seasonal_shopper', 'impulse_buyer']),
    frequency: Object.freeze({
      emailPerWeek: 3,
      emailPerMonth: 10,
      smsPerMonth: 6,
      minGapHours: 24
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 90,
      coPurchaseMinCount: 5,
      returnReasonCategories: Object.freeze({
        fit: ['too small', 'too large', 'too tight', 'too loose', 'wrong size', 'doesn\'t fit', 'sizing'],
        quality: ['defective', 'damaged', 'broken', 'poor quality', 'fell apart', 'cheap'],
        not_as_expected: ['not as described', 'different from picture', 'wrong color', 'not what i expected', 'looks different'],
        changed_mind: ['changed mind', 'don\'t want', 'no longer need', 'found better', 'impulse'],
        other: ['other', 'gift', 'duplicate', 'late delivery']
      }),
      sizePatterns: Object.freeze(['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL',
        '0', '2', '4', '6', '8', '10', '12', '14', '16',
        '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13']),
      reorderDueWindowDays: 14,
      discountSuccessMetric: 'conversion_rate',
      crossSellFraming: 'PAIRS WELL WITH:',
      sequentialPatternType: 'general'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.15,
      fullPriceBuyerThreshold: 0.05
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'sale_shopper',
      fullPriceBuyerName: 'full_price_buyer',
      frequentBrowserName: 'frequent_browser'
    }),
    contextFocus: Object.freeze(['lifecycle', 'value', 'churn_risk', 'purchase_history'])
  }),

  // ── Fashion Boutique ───────────────────────────────────────────
  fashion_boutique: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 8, minSpend: 1500 }),
      loyal: Object.freeze({ minOrders: 4, minSpend: 600 }),
      atRiskDays: 90,
      churnedDays: 180
    }),
    valueSegments: Object.freeze({ vip: 1500, high: 400, medium: 100 }),
    enabledSignals: Object.freeze({
      sizing: true,
      reorderCandidates: false,
      stylePreference: true,
      ingredientSensitivity: false,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['trend_follower', 'capsule_builder', 'seasonal_updater', 'sale_hunter']),
    frequency: Object.freeze({
      emailPerWeek: 3,
      emailPerMonth: 10,
      smsPerMonth: 4,
      minGapHours: 24
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 120,
      coPurchaseMinCount: 4,
      returnReasonCategories: Object.freeze({
        fit: ['too small', 'too large', 'too tight', 'too loose', 'wrong size', 'doesn\'t fit', 'sizing', 'runs small', 'runs large'],
        style: ['not my style', 'looks different', 'color off', 'wrong color', 'not flattering', 'unflattering'],
        quality: ['defective', 'damaged', 'poor quality', 'pilling', 'shrinkage', 'fading'],
        changed_mind: ['changed mind', 'don\'t want', 'found better', 'impulse'],
        other: ['other', 'gift', 'duplicate', 'late delivery']
      }),
      sizePatterns: Object.freeze(['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL',
        '0', '2', '4', '6', '8', '10', '12', '14', '16',
        '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13',
        '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '36', '38']),
      reorderDueWindowDays: null,
      discountSuccessMetric: 'conversion_rate',
      crossSellFraming: 'COMPLETE THE LOOK:',
      sequentialPatternType: 'seasonal'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.20,
      fullPriceBuyerThreshold: 0.05
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'sale_hunter',
      fullPriceBuyerName: 'full_price_buyer',
      frequentBrowserName: 'window_shopper'
    }),
    contextFocus: Object.freeze(['style_preference', 'sizing', 'seasonal_trends', 'lifecycle'])
  }),

  // ── Beauty & Wellness ──────────────────────────────────────────
  beauty_wellness: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 6, minSpend: 800 }),
      loyal: Object.freeze({ minOrders: 3, minSpend: 300 }),
      atRiskDays: 60,
      churnedDays: 120
    }),
    valueSegments: Object.freeze({ vip: 800, high: 300, medium: 80 }),
    enabledSignals: Object.freeze({
      sizing: false,
      reorderCandidates: true,
      stylePreference: false,
      ingredientSensitivity: true,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['routine_loyalist', 'ingredient_researcher', 'trend_tester', 'clean_beauty_advocate']),
    frequency: Object.freeze({
      emailPerWeek: 3,
      emailPerMonth: 12,
      smsPerMonth: 6,
      minGapHours: 20
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 180,
      coPurchaseMinCount: 3,
      returnReasonCategories: Object.freeze({
        reaction: ['allergic', 'irritation', 'breakout', 'rash', 'sensitivity', 'reaction', 'burning'],
        ineffective: ['didn\'t work', 'no results', 'no difference', 'ineffective', 'waste'],
        texture: ['too greasy', 'too thick', 'too thin', 'bad smell', 'texture', 'consistency'],
        changed_mind: ['changed mind', 'don\'t want', 'found better', 'wrong shade'],
        other: ['other', 'gift', 'duplicate', 'expired']
      }),
      sizePatterns: null,
      reorderDueWindowDays: 30,
      discountSuccessMetric: 'repeat_rate',
      crossSellFraming: 'COMPLETES YOUR ROUTINE:',
      sequentialPatternType: 'routine'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.10,
      fullPriceBuyerThreshold: 0.08
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'deal_seeker',
      fullPriceBuyerName: 'brand_loyalist',
      frequentBrowserName: 'ingredient_browser'
    }),
    contextFocus: Object.freeze(['routine_replenishment', 'ingredient_preferences', 'skin_type', 'lifecycle'])
  }),

  // ── Home & Decor ───────────────────────────────────────────────
  home_decor: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 6, minSpend: 2500 }),
      loyal: Object.freeze({ minOrders: 3, minSpend: 800 }),
      atRiskDays: 120,
      churnedDays: 270
    }),
    valueSegments: Object.freeze({ vip: 2500, high: 800, medium: 200 }),
    enabledSignals: Object.freeze({
      sizing: false,
      reorderCandidates: false,
      stylePreference: true,
      ingredientSensitivity: false,
      specComparison: false,
      projectTracking: true
    }),
    archetypes: Object.freeze(['room_refresher', 'project_buyer', 'seasonal_decorator', 'collector']),
    frequency: Object.freeze({
      emailPerWeek: 2,
      emailPerMonth: 7,
      smsPerMonth: 3,
      minGapHours: 48
    }),
    decisionHistoryDays: 60,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 365,
      coPurchaseMinCount: 3,
      returnReasonCategories: Object.freeze({
        size_fit: ['too large', 'too small', 'wrong dimensions', 'doesn\'t fit space', 'wrong size'],
        quality: ['defective', 'damaged', 'broken', 'poor quality', 'cheap looking'],
        color_match: ['wrong color', 'doesn\'t match', 'color off', 'different shade', 'not as pictured'],
        changed_mind: ['changed mind', 'redecorated', 'found better', 'wrong style'],
        other: ['other', 'gift', 'duplicate', 'late delivery']
      }),
      sizePatterns: null,
      reorderDueWindowDays: null,
      discountSuccessMetric: 'aov_lift',
      crossSellFraming: 'COORDINATES WITH:',
      sequentialPatternType: 'project'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.12,
      fullPriceBuyerThreshold: 0.06
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'sale_shopper',
      fullPriceBuyerName: 'design_enthusiast',
      frequentBrowserName: 'inspiration_browser'
    }),
    contextFocus: Object.freeze(['project_history', 'style_cohesion', 'room_coordination', 'lifecycle'])
  }),

  // ── Electronics & Tech ─────────────────────────────────────────
  electronics_tech: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 5, minSpend: 3000 }),
      loyal: Object.freeze({ minOrders: 3, minSpend: 1000 }),
      atRiskDays: 120,
      churnedDays: 240
    }),
    valueSegments: Object.freeze({ vip: 3000, high: 1000, medium: 200 }),
    enabledSignals: Object.freeze({
      sizing: false,
      reorderCandidates: false,
      stylePreference: false,
      ingredientSensitivity: false,
      specComparison: true,
      projectTracking: false
    }),
    archetypes: Object.freeze(['early_adopter', 'spec_researcher', 'ecosystem_buyer', 'budget_optimizer']),
    frequency: Object.freeze({
      emailPerWeek: 2,
      emailPerMonth: 7,
      smsPerMonth: 3,
      minGapHours: 48
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 180,
      coPurchaseMinCount: 4,
      returnReasonCategories: Object.freeze({
        defective: ['defective', 'broken', 'dead on arrival', 'doa', 'malfunction', 'not working'],
        spec_mismatch: ['wrong specs', 'not compatible', 'doesn\'t work with', 'incompatible', 'wrong model'],
        performance: ['too slow', 'poor battery', 'poor quality', 'not as advertised', 'underwhelming'],
        changed_mind: ['changed mind', 'found better', 'price dropped', 'upgraded'],
        other: ['other', 'gift', 'duplicate', 'late delivery']
      }),
      sizePatterns: null,
      reorderDueWindowDays: null,
      discountSuccessMetric: 'conversion_rate',
      crossSellFraming: 'COMPATIBLE ACCESSORIES:',
      sequentialPatternType: 'ecosystem'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.18,
      fullPriceBuyerThreshold: 0.04
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'deal_hunter',
      fullPriceBuyerName: 'early_adopter',
      frequentBrowserName: 'spec_browser'
    }),
    contextFocus: Object.freeze(['spec_comparison', 'ecosystem_compatibility', 'upgrade_cycle', 'lifecycle'])
  }),

  // ── Gift & Specialty ───────────────────────────────────────────
  gift_specialty: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 8, minSpend: 1000 }),
      loyal: Object.freeze({ minOrders: 4, minSpend: 400 }),
      atRiskDays: 90,
      churnedDays: 180
    }),
    valueSegments: Object.freeze({ vip: 1000, high: 400, medium: 100 }),
    enabledSignals: Object.freeze({
      sizing: false,
      reorderCandidates: false,
      stylePreference: true,
      ingredientSensitivity: false,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['thoughtful_gifter', 'last_minute_buyer', 'event_planner', 'self_treater']),
    frequency: Object.freeze({
      emailPerWeek: 3,
      emailPerMonth: 10,
      smsPerMonth: 5,
      minGapHours: 24
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 365,
      coPurchaseMinCount: 3,
      returnReasonCategories: Object.freeze({
        not_suitable: ['not suitable', 'wrong choice', 'not their taste', 'didn\'t like', 'not right'],
        quality: ['defective', 'damaged', 'broken', 'poor quality'],
        duplicate: ['duplicate', 'already has', 'got something else'],
        changed_mind: ['changed mind', 'found better', 'too expensive', 'wrong occasion'],
        other: ['other', 'late delivery', 'wrong item']
      }),
      sizePatterns: null,
      reorderDueWindowDays: null,
      discountSuccessMetric: 'conversion_rate',
      crossSellFraming: 'PERFECT ADDITIONS:',
      sequentialPatternType: 'occasion'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.12,
      fullPriceBuyerThreshold: 0.06
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'deal_seeker',
      fullPriceBuyerName: 'premium_gifter',
      frequentBrowserName: 'browsing_for_ideas'
    }),
    contextFocus: Object.freeze(['gifting_occasions', 'recipient_preferences', 'seasonal_events', 'lifecycle'])
  }),

  // ── Luxury & Premium ───────────────────────────────────────────
  luxury_premium: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 2, minSpend: 5000 }),
      loyal: Object.freeze({ minOrders: 2, minSpend: 2000 }),
      atRiskDays: 150,
      churnedDays: 365
    }),
    valueSegments: Object.freeze({ vip: 5000, high: 2000, medium: 500 }),
    enabledSignals: Object.freeze({
      sizing: true,
      reorderCandidates: false,
      stylePreference: true,
      ingredientSensitivity: false,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['connoisseur', 'status_buyer', 'investment_shopper', 'gifting_elite']),
    frequency: Object.freeze({
      emailPerWeek: 1,
      emailPerMonth: 4,
      smsPerMonth: 2,
      minGapHours: 72
    }),
    decisionHistoryDays: 90,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 365,
      coPurchaseMinCount: 2,
      returnReasonCategories: Object.freeze({
        authenticity: ['not authentic', 'fake', 'counterfeit', 'not genuine'],
        quality: ['defective', 'damaged', 'craftsmanship issue', 'poor finish', 'not premium'],
        fit: ['wrong size', 'doesn\'t fit', 'too small', 'too large'],
        expectation: ['not as expected', 'disappointing', 'not worth price', 'color different'],
        other: ['other', 'gift', 'duplicate', 'changed mind']
      }),
      sizePatterns: Object.freeze(['XS', 'S', 'M', 'L', 'XL',
        '36', '38', '40', '42', '44', '46', '48', '50',
        '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11']),
      reorderDueWindowDays: null,
      discountSuccessMetric: 'revenue_per_customer',
      crossSellFraming: 'CURATED FOR YOU:',
      sequentialPatternType: 'collection'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.08,
      fullPriceBuyerThreshold: 0.10
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'value_seeker',
      fullPriceBuyerName: 'connoisseur',
      frequentBrowserName: 'aspirational_browser'
    }),
    contextFocus: Object.freeze(['exclusivity', 'craftsmanship', 'brand_affinity', 'lifecycle'])
  }),

  // ── Consumables ────────────────────────────────────────────────
  consumables: Object.freeze({
    lifecycle: Object.freeze({
      vip: Object.freeze({ minOrders: 12, minSpend: 600 }),
      loyal: Object.freeze({ minOrders: 6, minSpend: 250 }),
      atRiskDays: 45,
      churnedDays: 90
    }),
    valueSegments: Object.freeze({ vip: 600, high: 250, medium: 60 }),
    enabledSignals: Object.freeze({
      sizing: false,
      reorderCandidates: true,
      stylePreference: false,
      ingredientSensitivity: true,
      specComparison: false,
      projectTracking: false
    }),
    archetypes: Object.freeze(['subscription_candidate', 'variety_seeker', 'bulk_buyer', 'health_conscious']),
    frequency: Object.freeze({
      emailPerWeek: 3,
      emailPerMonth: 12,
      smsPerMonth: 6,
      minGapHours: 20
    }),
    decisionHistoryDays: 30,
    orderIntelligence: Object.freeze({
      patternLookbackDays: 180,
      coPurchaseMinCount: 3,
      returnReasonCategories: Object.freeze({
        taste: ['bad taste', 'don\'t like flavor', 'wrong flavor', 'taste', 'disgusting'],
        reaction: ['allergic', 'upset stomach', 'side effects', 'reaction', 'intolerance'],
        ineffective: ['didn\'t work', 'no results', 'no difference', 'ineffective'],
        changed_mind: ['changed mind', 'found better', 'too expensive', 'switched brands'],
        other: ['other', 'expired', 'duplicate', 'late delivery']
      }),
      sizePatterns: null,
      reorderDueWindowDays: 21,
      discountSuccessMetric: 'repeat_rate',
      crossSellFraming: 'FREQUENTLY BOUGHT TOGETHER:',
      sequentialPatternType: 'replenishment'
    }),
    discountBehavior: Object.freeze({
      saleShopperThreshold: 0.12,
      fullPriceBuyerThreshold: 0.06
    }),
    segmentNames: Object.freeze({
      saleShopperName: 'deal_stocker',
      fullPriceBuyerName: 'brand_loyalist',
      frequentBrowserName: 'variety_browser'
    }),
    contextFocus: Object.freeze(['reorder_timing', 'consumption_patterns', 'dietary_preferences', 'lifecycle'])
  })
});

// ============================================
// Store Domain Classification
// ============================================

/**
 * Category-to-archetype mapping rules.
 * A store's archetype is determined by its dominant product categories.
 */
const CATEGORY_ARCHETYPE_MAP = {
  apparel: 'fashion_boutique',
  beauty_skincare: 'beauty_wellness',
  home_lifestyle: 'home_decor',
  electronics: 'electronics_tech',
  food_wellness: 'consumables'
};

/**
 * Classify a store's domain archetype from its product catalog.
 *
 * @param {Object} categoryDistribution - { category: percentage } e.g. { apparel: 0.8, home_lifestyle: 0.15, beauty_skincare: 0.05 }
 * @param {Object} catalogCharacteristics - { avgPrice, skuCount, hasSizedProducts, hasConsumables }
 * @returns {{ archetype: string, confidence: number }}
 */
function getStoreDomain(categoryDistribution, catalogCharacteristics = {}) {
  if (!categoryDistribution || Object.keys(categoryDistribution).length === 0) {
    return { archetype: 'general_retail', confidence: 0 };
  }

  const { avgPrice = 0, hasSizedProducts = false, hasConsumables = false } = catalogCharacteristics;

  // Sort categories by percentage descending
  const sorted = Object.entries(categoryDistribution)
    .filter(([, pct]) => pct > 0)
    .sort(([, a], [, b]) => b - a);

  if (sorted.length === 0) {
    return { archetype: 'general_retail', confidence: 0 };
  }

  const [topCategory, topPct] = sorted[0];
  const secondPct = sorted.length > 1 ? sorted[1][1] : 0;

  // Check for luxury override based on average price
  if (avgPrice >= 200 && topPct >= 0.4) {
    return { archetype: 'luxury_premium', confidence: Math.min(0.95, 0.6 + (avgPrice / 1000)) };
  }

  // Strong single-category dominance (>60%)
  if (topPct >= 0.6) {
    const archetype = CATEGORY_ARCHETYPE_MAP[topCategory];
    if (archetype) {
      return { archetype, confidence: Math.min(0.98, topPct + 0.1) };
    }
  }

  // Moderate dominance (40-60%) with supporting signals
  if (topPct >= 0.4) {
    const archetype = CATEGORY_ARCHETYPE_MAP[topCategory];
    if (archetype) {
      let confidence = topPct;

      // Boost confidence with supporting signals
      if (topCategory === 'apparel' && hasSizedProducts) confidence += 0.1;
      if (topCategory === 'food_wellness' && hasConsumables) confidence += 0.1;

      return { archetype, confidence: Math.min(0.95, confidence) };
    }
  }

  // Check for gift/specialty: highly mixed catalog with no dominant category
  if (topPct < 0.4 && sorted.length >= 3) {
    const genericCount = sorted.filter(([cat]) => cat === 'generic').reduce((sum, [, pct]) => sum + pct, 0);
    if (genericCount >= 0.3 || (topPct - secondPct < 0.1)) {
      return { archetype: 'gift_specialty', confidence: 0.5 };
    }
  }

  // Weak signal — map if we have any match
  if (topPct >= 0.3) {
    const archetype = CATEGORY_ARCHETYPE_MAP[topCategory];
    if (archetype) {
      return { archetype, confidence: topPct };
    }
  }

  return { archetype: 'general_retail', confidence: 0.3 };
}

/**
 * Get the effective config for a store domain archetype.
 * Falls back to general_retail if archetype is unknown or undefined.
 *
 * @param {string} [archetype] - Store domain archetype key
 * @returns {Object} Frozen archetype config
 */
function getEffectiveConfig(archetype) {
  if (!archetype || !ARCHETYPE_CONFIGS[archetype]) {
    return ARCHETYPE_CONFIGS.general_retail;
  }
  return ARCHETYPE_CONFIGS[archetype];
}

module.exports = {
  CATEGORY_MAP,
  getProductCategory,
  ARCHETYPE_NAMES,
  ARCHETYPE_CONFIGS,
  getStoreDomain,
  getEffectiveConfig
};
